import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../../services/apiService';
import { recalculateAccountBalances } from '../../../services/accountService';
import dayjs from 'dayjs';
import VoucherTemplateModal from './VoucherTemplateModal';

// واجهة نموذج الحساب
interface Account {
  id: string;
  accountNumber: string;
  name: string;
  type: string;
  balance?: number;
  currentBalance?: number;
  initialBalance: number;
  isActive: boolean;
  createdAt: string;
  parentAccount: string | null;
  parentAccountName?: string | null;
  level?: number;           // المستوى في الشجرة الهرمية
  children?: Account[];     // الحسابات الفرعية المباشرة
  isExpanded?: boolean;     // حالة العرض (موسع أو مطوي)
}

const AccountsList: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();

  // تحويل قائمة مسطحة إلى بنية شجرية
  const buildAccountTree = (accounts: Account[]): Account[] => {
    // نسخة من القائمة لعدم تعديل الأصل
    const accountsCopy = JSON.parse(JSON.stringify(accounts)) as Account[];
    
    // إنشاء خريطة للحسابات حسب المعرف للوصول السريع
    const accountMap = new Map<string, Account>();
    accountsCopy.forEach(account => {
      account.level = 0;
      account.children = [];
      account.isExpanded = true;
      accountMap.set(account.id, account);
    });
    
    // إنشاء الهيكل الشجري
    const rootAccounts: Account[] = [];
    
    accountsCopy.forEach(account => {
      // حساب الرصيد الحالي مع الأطفال
      account.balance = account.currentBalance || 0;
      
      if (account.parentAccount) {
        // الحساب لديه أب
        const parent = accountMap.get(account.parentAccount);
        if (parent) {
          // إضافة الحساب إلى قائمة أبناء الأب
          parent.children!.push(account);
          // ضبط مستوى الحساب (مستوى الأب + 1)
          account.level = (parent.level || 0) + 1;
        } else {
          // إذا لم يتم العثور على الأب، نضع الحساب في الجذر
          rootAccounts.push(account);
        }
      } else {
        // الحساب هو جذر (بدون أب)
        rootAccounts.push(account);
      }
    });
    
    // فرز الحسابات في كل مستوى حسب رقم الحساب
    const sortAccountsByNumber = (accounts: Account[]) => {
      accounts.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
      accounts.forEach(account => {
        if (account.children && account.children.length > 0) {
          sortAccountsByNumber(account.children);
        }
      });
    };
    
    sortAccountsByNumber(rootAccounts);
    return rootAccounts;
  };
  
  // تحويل الشجرة إلى قائمة مسطحة للعرض
  const flattenAccountTree = (accounts: Account[]): Account[] => {
    const result: Account[] = [];
    
    const flatten = (account: Account, depth = 0) => {
      account.level = depth;
      result.push(account);
      
      if (account.children && account.children.length > 0 && account.isExpanded) {
        account.children.forEach(child => flatten(child, depth + 1));
      }
    };
    
    accounts.forEach(account => flatten(account, 0));
    return result;
  };
  
  // وظيفة تبديل حالة التوسعة لحساب
  const toggleExpand = (accountId: string) => {
    const updateExpanded = (accounts: Account[]): Account[] => {
      return accounts.map(account => {
        if (account.id === accountId) {
          return {
            ...account,
            isExpanded: !account.isExpanded
          };
        }
        
        if (account.children && account.children.length > 0) {
          return {
            ...account,
            children: updateExpanded(account.children)
          };
        }
        
        return account;
      });
    };
    
    setAccounts(updateExpanded(accounts));
  };
  
  // وظيفة البحث في شجرة الحسابات
  const searchInTree = (accounts: Account[], term: string): Account[] => {
    const result: Account[] = [];
    const lowerTerm = term.toLowerCase();
    
    const search = (account: Account): boolean => {
      // البحث في الاسم ورقم الحساب
      const nameMatch = account.name.toLowerCase().includes(lowerTerm);
      const numberMatch = account.accountNumber.toLowerCase().includes(lowerTerm);
      
      if (nameMatch || numberMatch) {
        // إذا تطابق الحساب نفسه، نضيفه مباشرة
        result.push({ ...account, isExpanded: true, children: account.children });
        return true;
      }
      
      // البحث في الأبناء
      if (account.children && account.children.length > 0) {
        const matchingChildren = account.children
          .map(child => ({ ...child, isExpanded: true }))
          .filter(child => search(child));
        
        if (matchingChildren.length > 0) {
          // إذا كان هناك أبناء مطابقون، نضيف الوالد أيضًا
          const accountCopy = {
            ...account,
            children: matchingChildren,
            isExpanded: true
          };
          result.push(accountCopy);
          return true;
        }
      }
      
      return false;
    };
    
    accounts.forEach(search);
    return result;
  };
  
  // تطبيق البحث على شجرة الحسابات
  const filteredAccountTree = searchTerm ? searchInTree(accounts, searchTerm) : accounts;
  
  // تحويل الشجرة المفلترة إلى قائمة مسطحة للعرض
  const filteredAccounts = flattenAccountTree(filteredAccountTree);

  // وظيفة إعادة تحميل البيانات
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get<Account[]>('/finance/accounts');
      // تصفية الحسابات لإظهار النشطة فقط
      const activeAccounts = response.filter(account => account.isActive);
      const accountTree = buildAccountTree(activeAccounts);
      setAccounts(accountTree);
      setLoading(false);
    } catch (err: any) {
      console.error('خطأ في تحميل الحسابات:', err);
      setError(err.response?.data?.message || 'حدث خطأ في تحميل بيانات الحسابات');
      setLoading(false);
    }
  };

  // وظيفة الانتقال إلى صفحة معاملات الحساب
  const handleOpenTransactions = (account: Account) => {
    navigate(`/finance/accounts/${account.id}/transactions`);
  };

  // وظيفة حذف الحساب
  const handleDeleteAccount = async () => {
    if (selectedAccount) {
      setLoading(true);
      
      try {
        // التحقق مما إذا كان الحساب يحتوي على أبناء
        const hasChildren = selectedAccount.children && selectedAccount.children.length > 0;
        
        if (hasChildren) {
          setError('لا يمكن حذف الحساب لأنه يحتوي على حسابات فرعية. يرجى حذف الحسابات الفرعية أولاً.');
          setLoading(false);
          return;
        }
        
        // استدعاء API لحذف الحساب
        await apiService.delete(`/finance/accounts/${selectedAccount.id}`);
        
        // إغلاق مربع الحوار قبل تحديث القائمة
        setConfirmDeleteOpen(false);
        setSelectedAccount(null);
        
        // تحديث القائمة بعد الحذف
        handleRefresh();
      } catch (err: any) {
        console.error('خطأ في حذف الحساب:', err);
        setError(err.response?.data?.message || 'حدث خطأ في حذف الحساب');
        setLoading(false);
        setConfirmDeleteOpen(false);
      }
    }
  };

  // وظيفة إعادة حساب الأرصدة
  const handleRecalculateBalances = async () => {
    if (!window.confirm('هل تريد إعادة حساب أرصدة جميع الحسابات؟\n\nملاحظة: قد تستغرق هذه العملية بعض الوقت حسب عدد المعاملات.\nسيتم معالجة البيانات على دفعات لحماية الخادم.')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await recalculateAccountBalances();
      
      if (result.success) {
        const message = result.data.processedAccounts
          ? `تم إعادة حساب الأرصدة بنجاح! ✅\n\n` +
            `📊 عدد الحسابات: ${result.data.accountsCount}\n` +
            `✔️ الحسابات المعالجة: ${result.data.processedAccounts}\n` +
            `📝 إجمالي المعاملات: ${result.data.totalTransactions}\n` +
            `🔄 المعاملات المحدثة: ${result.data.totalUpdates}`
          : `تم إعادة حساب الأرصدة بنجاح! ✅\n\n` +
            `📊 الحساب: ${result.data.accountName}\n` +
            `📝 عدد المعاملات: ${result.data.transactionsCount}\n` +
            `🔄 المعاملات المحدثة: ${result.data.updatedCount}\n` +
            `💰 الرصيد النهائي: ${result.data.finalBalance} ريال`;
        
        alert(message);
        // إعادة تحميل البيانات
        handleRefresh();
      } else {
        setError(result.message || 'فشلت عملية إعادة حساب الأرصدة');
      }
    } catch (err: any) {
      console.error('خطأ في إعادة حساب الأرصدة:', err);
      setError(err.message || 'حدث خطأ أثناء إعادة حساب الأرصدة');
      setLoading(false);
    }
  };

  // عرض أيقونة الحالة
  const renderStatusChip = (isActive: boolean) => (
    <Chip 
      size="small" 
      label={isActive ? 'نشط' : 'غير نشط'} 
      color={isActive ? 'success' : 'error'}
      sx={{ 
        fontWeight: 500,
        fontSize: '0.75rem'
      }}
    />
  );

  // عرض نوع الحساب
  const renderAccountType = (type: string) => (
    <Chip 
      size="small" 
      label={type} 
      color="primary"
      variant="outlined"
      sx={{ 
        fontWeight: 500,
        fontSize: '0.75rem'
      }}
    />
  );

  // عرض هامش المستوى للحسابات المتداخلة
  const renderIndent = (level: number = 0) => (
    <Box sx={{ 
      width: level * 20, 
      display: 'inline-block' 
    }} />
  );

  // عرض أيقونة التوسعة
  const renderExpandIcon = (account: Account) => {
    if (!account.children || account.children.length === 0) {
      return <Box sx={{ width: 24, display: 'inline-block' }} />;
    }
    
    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          toggleExpand(account.id);
        }}
        sx={{ p: 0.5 }}
      >
        <Box component="span" sx={{ fontSize: '1.2rem' }}>
          {account.isExpanded ? '−' : '+'}
        </Box>
      </IconButton>
    );
  };

  // تحميل البيانات عند تحميل المكون
  useEffect(() => {
    handleRefresh();
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={2} sx={{ p: 3 }}>
        {/* رأس الصفحة مع أدوات البحث والتحكم */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              الحسابات النشطة
            </Typography>
            
            <Button
              variant="outlined"
              color="primary"
              component={Link}
              to="/finance/accounts/inactive"
              sx={{ borderRadius: '8px' }}
            >
              الحسابات غير النشطة
            </Button>
            
            <Button
              variant="outlined"
              color="primary"
              startIcon={<AccountBalanceIcon />}
              component={Link}
              to="/finance/accounts/payment-methods"
              sx={{ borderRadius: '8px' }}
            >
              طرق الدفع
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<BookmarkBorderIcon />}
              onClick={() => setTemplateModalOpen(true)}
              sx={{ borderRadius: '8px' }}
            >
              قوالب السندات
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              placeholder="بحث..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{ width: 250 }}
            />
            
            <Tooltip title="تحديث" arrow>
              <IconButton
                onClick={handleRefresh}
                color="primary"
                disabled={loading}
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="إعادة حساب الأرصدة" arrow>
              <IconButton
                onClick={handleRecalculateBalances}
                color="secondary"
                disabled={loading}
                sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1) }}
              >
                <CalculateIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={Link}
              to="/finance/accounts/new"
              sx={{ borderRadius: '8px' }}
            >
              إضافة حساب
            </Button>
          </Box>
        </Box>
        
        {/* عرض الأخطاء */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {/* جدول الحسابات */}
        <TableContainer sx={{ 
          maxHeight: 'calc(100vh - 260px)', 
          overflowY: 'auto',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          borderRadius: 1
        }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                <TableCell>الحساب</TableCell>
                <TableCell>النوع</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell align="right">الرصيد الابتدائي</TableCell>
                <TableCell align="right">الرصيد الحالي</TableCell>
                <TableCell>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
                  </TableCell>
                </TableRow>
              ) : filteredAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      لا توجد حسابات نشطة متطابقة مع البحث
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccounts.map((account) => (
                  <TableRow 
                    key={account.id} 
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      bgcolor: account.level && account.level > 0 ? 
                        alpha(theme.palette.background.paper, 0.5) : 'transparent'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {renderIndent(account.level)}
                        {renderExpandIcon(account)}
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {account.accountNumber} - {account.name}
                          </Typography>
                          {account.parentAccountName && (
                            <Typography variant="caption" color="text.secondary">
                              الحساب الرئيسي: {account.parentAccountName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{renderAccountType(account.type)}</TableCell>
                    <TableCell>{renderStatusChip(account.isActive)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {account.initialBalance.toLocaleString()}
                    </TableCell>
                    <TableCell align="right" sx={{ 
                      fontWeight: 500,
                      color: (account.currentBalance || 0) < 0 ? 'error.main' : 'success.main'
                    }}>
                      {(account.currentBalance || 0).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="عرض المعاملات" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTransactions(account);
                            }}
                            sx={{ 
                              color: theme.palette.info.main,
                              '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="تعديل" arrow>
                          <IconButton 
                            size="small"
                            component={Link}
                            to={`/finance/accounts/edit/${account.id}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ 
                              color: theme.palette.primary.main,
                              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="حذف" arrow>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAccount(account);
                              setConfirmDeleteOpen(true);
                            }}
                            sx={{ 
                              color: theme.palette.error.main,
                              '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* حوار تأكيد الحذف */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>
          تأكيد الحذف
        </DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من رغبتك في حذف الحساب {selectedAccount?.name} ؟
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            لا يمكن التراجع عن هذا الإجراء. سيتم حذف الحساب نهائيًا.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDeleteOpen(false)}
            color="inherit"
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* نافذة قوالب السندات */}
      <VoucherTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        onSave={() => {
          // يمكن إضافة أي إجراء بعد الحفظ إذا لزم الأمر
        }}
      />
    </Box>
  );
};

export default AccountsList;